// Receta de fusión de un campo: copia en JS de la semántica del motor de materialización
// (MaterializeEngine.newValue/keyValue/apply y MaterializeValues.convert/concat/isEmpty/same).
// Si se toca el motor, se toca también este fichero (y los textos rc.*). Puro: sin Vue ni catálogo,
// todo llega por parámetros (se puede probar con Node).
import { t as tr, locale } from './i18n.js';

// «de el código…» → «del código…» (los datos de la encuesta entran con artículo en frases con «de»/«a»).
const t = (key, params) => tr(key, params).replace(/([Dd]e|[Aa]) el/g, (m, p) => (p.toLowerCase() === 'de' ? p[0] + 'el' : p[0] + 'l'));

const MODES = ['last', 'first', 'priority', 'concat'];
const LEVELS = { text: 0, info: 1, warn: 2, danger: 3 };
const STATUS = ['ok', 'info', 'warn', 'danger'];
const INTL = { es: 'es-ES', ca: 'ca-ES', en: 'en-GB' };
const FAMILIES = {
  String: 'text', Integer: 'int', int: 'int', Long: 'int', long: 'int', BigDecimal: 'dec', Double: 'dec', double: 'dec',
  Boolean: 'bool', boolean: 'bool', LocalDate: 'date', LocalDateTime: 'datetime', Timestamp: 'datetime', Date: 'dateAny',
};
const NUM_RE = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
const THOUSANDS_RE = /^\d{1,3}[.,]\d{3}$/;
const BOOLS = { yes: true, si: true, 'sí': true, s: true, y: true, true: true, 1: true, no: false, n: false, false: false, 0: false };
// [expresión, posición de año, mes y día]; el motor prueba primero los formatos con hora (mismo resultado)
const DATE_RES = [
  [/^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/, 3, 2, 1],
  [/^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}):(\d{2}))?$/, 1, 2, 3],
  [/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/, 1, 2, 3],
];
const CANON_RE = /^(\d{4})-(\d{2})-(\d{2})(?: (\d{2}):(\d{2}):(\d{2}))?$/;
const DAYS = ['2024-03-03', '2024-03-10', '2024-04-02', '2024-04-15', '2024-05-06'];
// muestras v1–v4 y valor de partida por familia (F.5)
const SAMPLES = {
  text: [['Clínica Norte', 'Clínica del Norte', 'Clínica Norte S.L.', 'C. Norte'], 'Clínica Antigua'],
  int: [['120', '135', '140', '150'], '100'],
  dec: [['4,25', '4,30', '4,5', '4,75'], '4,00'],
  bool: [['sí', 'no', 'sí', 'no'], 'no'],
  date: [['01/03/2024', '2024-06-15', '15/09/2024', '2024-12-01'], '01/01/2023'],
  datetime: [['01/03/2024 10:00:00', '2024-06-15 09:30:00', '15/09/2024 18:45:00', '2024-12-01 08:00:00'], '01/01/2023 00:00:00'],
  dateAny: [['01/03/2024', '2024-06-15', '15/09/2024', '2024-12-01'], '01/01/2023'],
  unsupported: [['abc', 'def', 'ghi', 'jkl'], 'abc'],
};
const CONV_RAW = {
  text: [],
  int: ['12', '12,0', '12,5', '1.000', 'doce'],
  dec: ['4,25', '4.25', '1.234,5', 'n/d'],
  bool: ['Sí', '1', 'N', 'quizá'],
  date: ['01/03/2024', '2024-03-01', '1/3/2024', '01-03-2024'],
  datetime: ['01/03/2024 10:30:00', '2024-03-01T10:30:00', '01/03/2024', '01/03/2024 10:30'],
  dateAny: ['01/03/2024', '2024-03-01 10:30:00', '1/3/2024'],
  unsupported: [],
};

const intlLocale = () => INTL[locale.value] || INTL.es;
const shortType = (type) => { const s = String(type || ''); return s.slice(s.lastIndexOf('.') + 1); };
const hasText = (v) => v != null && String(v).trim() !== '';
const pad = (n, w = 2) => String(n).padStart(w, '0');
const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysIn = (y, m) => (m === 2 ? (isLeap(y) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31);
const typeLabel = (family, typeName) => (family === 'unsupported' ? typeName || '?' : t('type.' + family));

/** Familia de conversión de un tipo Java (el catálogo da getSimpleName()). */
export function typeFamily(type) {
  return FAMILIES[shortType(type)] || 'unsupported';
}

/** ¿Es un dato de la encuesta (@quest_id, @response_id, @response_date)? */
export const isSys = (a) => a.startsWith('@');

/** Copia de MaterializeValues.isEmpty: null o texto en blanco. */
export function isEmpty(v) {
  return v == null || (typeof v === 'string' && v.trim() === '');
}

// MaterializeValues.same: fechas por instante (una fecha sola = 00:00:00), números por valor
// (4,30 = 4,3, como BigDecimal.compareTo) y el resto por igualdad.
function same(a, b, isText) {
  if (a == null || b == null) return a == null && b == null;
  if (!isText && typeof a === 'string' && typeof b === 'string' && CANON_RE.test(a) && CANON_RE.test(b)) {
    return (a.length === 10 ? a + ' 00:00:00' : a) === (b.length === 10 ? b + ' 00:00:00' : b);
  }
  return a === b;
}

// MaterializeValues.tokens: un valor simple es un elemento; «|a|b|» se parte, sin elementos en blanco
function tokens(v) {
  if (!hasText(v)) return [];
  const s = String(v);
  return s.startsWith('|') ? s.split('|').filter(hasText) : [s];
}

/** Copia de MaterializeValues.concat: une sin repetir (igualdad exacta) y siempre como |a|b|. */
export function concatValues(current, v) {
  return '|' + [...new Set([...tokens(current), ...tokens(v)])].join('|') + '|';
}

// parseDateTime del motor (ResolverStyle.SMART): día recortado al último del mes y 24:00:00 = día siguiente
function parseDate(s) {
  for (const [re, iy, im, id] of DATE_RES) {
    const g = re.exec(s);
    if (!g) continue;
    let y = +g[iy], m = +g[im], d = +g[id];
    const timed = g[4] != null;
    let hh = timed ? +g[4] : 0;
    const mi = timed ? +g[5] : 0, ss = timed ? +g[6] : 0;
    if (y < 1 || m < 1 || m > 12 || d < 1 || d > 31 || hh > 24 || mi > 59 || ss > 59 || (hh === 24 && (mi || ss))) return null;
    d = Math.min(d, daysIn(y, m));
    if (hh === 24) {
      hh = 0;
      if (++d > daysIn(y, m)) { d = 1; if (++m > 12) { m = 1; y++; } }
    }
    return { date: `${pad(y, 4)}-${pad(m)}-${pad(d)}`, time: `${pad(hh)}:${pad(mi)}:${pad(ss)}`, timed };
  }
  return null;
}

/**
 * Copia de MaterializeValues.convert para una respuesta. Las fechas salen canónicas
 * (yyyy-MM-dd[ HH:mm:ss]); los números, como Number.
 * @returns {{ok:boolean, value?:any, thousands?:boolean}} thousands: «1.000» en un entero (vale 1)
 */
export function convertSample(family, raw) {
  const s = String(raw ?? '').trim();
  switch (family) {
    case 'text':
      return { ok: true, value: s };
    case 'int':
    case 'dec': {
      const n = s.replace(/,/g, '.');
      if (!NUM_RE.test(n)) return { ok: false };
      const value = Number(n);
      if (family === 'dec') return { ok: Number.isFinite(value), value };
      // intValueExact: sin decimales y dentro del rango de Integer
      if (!Number.isInteger(value) || value < -2147483648 || value > 2147483647) return { ok: false };
      return THOUSANDS_RE.test(s) ? { ok: true, value, thousands: true } : { ok: true, value };
    }
    case 'bool': {
      const b = BOOLS[s.toLowerCase()];
      return b == null ? { ok: false } : { ok: true, value: b };
    }
    case 'date':
    case 'datetime':
    case 'dateAny': {
      const p = parseDate(s);
      if (!p) return { ok: false };
      const full = family === 'datetime' || (family === 'dateAny' && p.timed);
      return { ok: true, value: full ? `${p.date} ${p.time}` : p.date };
    }
    default:
      return { ok: false };
  }
}

/**
 * Copia de MaterializeEngine.apply sobre el estado de un campo `st = {value, ev: null|{prio}}`.
 * @param {any} v valor convertido (null = la encuesta no lo trae)
 * @returns {{code:string, params?:object}} code = sufijo de la clave ex.*
 */
export function step(st, v, prio, mode, clear, isText) {
  const before = st.value;
  if (v == null) {
    if (clear) st.value = null;
    return { code: clear && !isEmpty(before) ? 'clear' : 'keep' };
  }
  if (mode === 'first') {
    if (!isEmpty(before)) return { code: 'ignoredFirst' };
    st.value = v;
    return { code: 'fill' };
  }
  if (mode === 'priority') {
    const ev = st.ev;
    if (!(ev == null || isEmpty(before) || prio <= ev.prio)) return { code: 'ignoredPrio', params: { p: prio, q: ev.prio } };
    st.value = v;
    st.ev = { prio };
    if (same(before, v, isText)) return { code: ev == null || ev.prio !== prio ? 'sameEv' : 'same', params: { p: prio } };
    return { code: isEmpty(before) ? 'fill' : ev == null ? 'noEvidence' : 'prioWins', params: { p: prio, q: ev?.prio } };
  }
  if (mode === 'concat' && isText) {
    st.value = concatValues(before, v);
    return { code: same(before, st.value, true) ? 'concatDup' : 'concat' };
  }
  st.value = v; // last, y concat en campos que no son texto
  return { code: isEmpty(before) ? 'fill' : same(before, v, isText) ? 'same' : 'set' };
}

/** Valor de un dato de la encuesta en el ejemplo (i = posición de la encuesta). */
export function sysValue(sys, e, i = 0) {
  if (sys === '@quest_id') return e.questId;
  if (sys === '@response_id') return 'RQH-' + pad(i + 1, 10);
  if (sys === '@response_date') return e.day + ' 10:00:00';
  return null;
}

/** Valor convertido listo para pintar (sí/no, coma decimal según idioma, fechas dd/mm/aaaa). */
export function showValue(family, v) {
  if (v == null) return '';
  if (typeof v === 'boolean') return t(v ? 'ex.yes' : 'ex.no');
  if (typeof v === 'number') return new Intl.NumberFormat(intlLocale(), { useGrouping: false, maximumFractionDigits: 20 }).format(v);
  const g = family !== 'text' && CANON_RE.exec(v);
  return g ? `${g[3]}/${g[2]}/${g[1]}${g[4] ? ` ${g[4]}:${g[5]}:${g[6]}` : ''}` : String(v);
}

// «3 mar» / «3 Mar»: día del ejemplo en el idioma activo
function dayLabel(day) {
  const g = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day));
  if (!g) return String(day ?? '');
  const [y, m, d] = g.slice(1).map(Number);
  return new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(Date.UTC(y, m - 1, d));
}

/**
 * Ejecuta el ejemplo: las encuestas en orden, como newValue (primer alias con valor; si no tiene,
 * el dato de la encuesta `sys` si la cadena lo lleva) + convert + apply.
 * @param {{events:Array<{day, questId, prio, raw}>, start:string|null, mode, clear:boolean, family,
 *          sys?:string|null, stateOf?:(questId)=>string, typeName?:string, formName?:(questId)=>string}} args
 * @returns {{rows:Array<{day, dayLabel, questId, prio, raw, before, after, beforeText, afterText, code, params,
 *            thousands:boolean, silent:boolean, text:string, silentText:string|null}>, final:any, finalText:string}}
 */
export function runExample({ events, start, mode, clear, family, sys = null, stateOf = () => 'feeds', typeName, formName }) {
  const isText = family === 'text';
  const c0 = start == null ? null : convertSample(family, start);
  const st = { value: c0 == null ? null : c0.ok ? c0.value : start, ev: null };
  const rows = events.map((e, i) => {
    const silent = stateOf(e.questId) === 'silent';
    const raw = silent ? null : hasText(e.raw) ? e.raw : sys ? sysValue(sys, e, i) : null;
    const before = st.value;
    let c = null;
    let r;
    if (hasText(raw)) {
      c = convertSample(family, raw);
      // la encuesta entera queda en error: el estado no cambia
      r = c.ok ? step(st, c.value, e.prio, mode, clear, isText) : { code: 'error', params: { raw: raw.trim(), type: typeLabel(family, typeName) } };
    } else {
      r = step(st, null, e.prio, mode, clear, isText);
    }
    return {
      day: e.day, dayLabel: dayLabel(e.day), questId: e.questId, prio: e.prio, raw,
      before, after: st.value, beforeText: showValue(family, before), afterText: showValue(family, st.value),
      code: r.code, params: r.params || null, thousands: !!c?.thousands, silent,
      text: t('ex.' + r.code, r.params), silentText: silent && formName ? t('ex.silent', { form: formName(e.questId) }) : null,
    };
  });
  return { rows, final: st.value, finalText: showValue(family, st.value) };
}

/** Valor final del ejemplo con cada regla × keep/clear. */
export function ruleMatrix(args) {
  const out = {};
  for (const mode of MODES) {
    out[mode] = { keep: runExample({ ...args, mode, clear: false }).final, clear: runExample({ ...args, mode, clear: true }).final };
  }
  return out;
}

/** Combinaciones de la matriz que generan un aviso («!»). */
export function ruleFlags({ family, N, ladderLen }) {
  const multi = N >= 2;
  return {
    last: { keep: false, clear: false },
    first: { keep: false, clear: true },
    priority: { keep: multi && ladderLen === 1, clear: multi },
    concat: { keep: family !== 'text', clear: true },
  };
}

const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const flat = (s) => norm(s).replace(/[^a-z0-9]/g, '');
const words = (s) => [...new Set(norm(String(s ?? '').replace(/([a-z0-9])([A-Z])/g, '$1 $2')).split(/[^a-z0-9]+/).filter(Boolean))];
const tokMatch = (x, y) => x === y || (Math.min(x.length, y.length) >= 3 && (x.startsWith(y) || y.startsWith(x)));

function jaccard(a, b) {
  if (!a.length || !b.length) return 0;
  const m = a.filter((x) => b.some((y) => tokMatch(x, y))).length;
  return m / (a.length + b.length - m);
}

// prefijo común más largo de los alias que acaba en «_» (p. ej. market_center_)
function commonPrefix(aliases) {
  if (aliases.length < 2) return '';
  let p = aliases[0];
  for (const a of aliases) while (!a.startsWith(p)) p = p.slice(0, -1);
  return p.slice(0, p.lastIndexOf('_') + 1);
}

/**
 * Preguntas sugeridas para un campo por parecido de nombre (nunca conecta sola).
 * @param {Array<{alias:string, label?:string, inField?:boolean, connected?:boolean}>} options
 *        inField = ya conectada a este campo (se excluye); connected = conectada en algún sitio
 * @returns {string[]} alias, de más a menos parecido
 */
export function suggestAliases(fieldName, options, max = 3) {
  const seen = new Map();
  for (const o of options) if (!seen.has(o.alias)) seen.set(o.alias, o);
  const opts = [...seen.values()];
  const prefix = commonPrefix(opts.map((o) => o.alias).filter((a) => !isSys(a)));
  const f = flat(fieldName);
  const ft = words(fieldName);
  return opts
    .filter((o) => !o.inField)
    .map((o) => {
      const a = prefix && o.alias.startsWith(prefix) ? o.alias.slice(prefix.length) : o.alias;
      const cands = [flat(a), flat(o.label)].filter(Boolean);
      const score = cands.includes(f) ? 1
        : cands.some((c) => Math.min(c.length, f.length) >= 3 && (c.includes(f) || f.includes(c))) ? 0.85
          : Math.max(jaccard(ft, words(a)), jaccard(ft, words(o.label)));
      return { alias: o.alias, score, rank: score + (o.connected ? 0 : 0.05) };
    })
    .filter((s) => s.score >= 0.6)
    .sort((x, y) => y.rank - x.rank)
    .slice(0, max)
    .map((s) => s.alias);
}

/** Ejemplos ✓/✗/! de conversión por familia: {raw, ok, value, mark:'ok'|'bad'|'warn'}. */
export const CONV_SAMPLES = Object.fromEntries(Object.entries(CONV_RAW).map(([fam, raws]) => [fam, raws.map((raw) => {
  const c = convertSample(fam, raw);
  return { raw, ok: c.ok, value: c.ok ? c.value : null, mark: !c.ok ? 'bad' : c.thousands ? 'warn' : 'ok' };
})]));

// «a, b y c»; con más de 4, «a, b, c, d y 2 más»
function listOf(items) {
  if (items.length > 4) return t('list.more', { list: items.slice(0, 4).join(', '), k: items.length - 4 });
  return items.length < 2 ? items.join('') : items.slice(0, -1).join(', ') + t('list.and') + items[items.length - 1];
}

/**
 * Receta de un campo: cómo lo calculará el motor (MaterializeEngine.newValue/keyValue/apply +
 * MaterializeValues.convert/concat/isEmpty) a partir de la definición en edición.
 *
 * @param {object}   ctx
 * @param {{id, cls, name, type, column, table, isExt, rootName, keyRole:'id'|'origin'|null}} ctx.field
 * @param {{mergeMode, onMissing}|null} ctx.rule                 null = last/keep
 * @param {string[]} ctx.chain                                   alias conectados, en el orden de dto.maps
 * @param {object[]} ctx.sources                                 dto.sources (orden del array)
 * @param {(questId:string)=>Set<string>|null} ctx.formAliases   alias del formulario activo (null = no activo)
 * @param {(questId:string)=>string} ctx.formName                nombre (con número si se repite)
 * @param {(alias:string)=>string|null} ctx.aliasLabel           etiqueta preferente o null
 * @returns {{status, compact:{rule, clear, cover, coverTitle}, line, sentences, warnings, perForm, keyRows, example}}
 */
export function buildRecipe(ctx) {
  const { field, chain = [], sources = [], formAliases, formName, aliasLabel } = ctx;
  const fam = typeFamily(field.type);
  const isText = fam === 'text';
  const mode = ctx.rule?.mergeMode || 'last';
  const clear = ctx.rule?.onMissing === 'clear';
  const type = typeLabel(fam, shortType(field.type));
  const sentences = [];
  const say = (group, code, params, level = 'text') => sentences.push({ code, group, level, text: t(code, params) });
  const q = (a) => (isSys(a) ? t('sysIn.' + a.slice(1)) : `«${aliasLabel(a) ?? a}»`);
  const chainOf = (as) => as.map(q).join(' › ');
  const prio = (s) => s.priority ?? 1;
  const name = (s) => formName(s.questId);
  const ordered = sources.map((s, i) => [s, i]).sort((x, y) => prio(x[0]) - prio(y[0]) || x[1] - y[1]).map(([s]) => s);
  const noCompact = { rule: null, clear: null, cover: null, coverTitle: null };
  const finish = (r) => ({ warnings: sentences.filter((s) => s.level === 'warn' || s.level === 'danger').map(({ code, level, text }) => ({ code, level, text })), ...r, sentences });

  // 1. Campo de la clave: lo fija keyValue (pregunta de la fuente o, para el origen, su texto fijo)
  if (field.keyRole) {
    const isId = field.keyRole === 'id';
    say('K', isId ? 'rc.key.id' : 'rc.key.origin');
    const keyRows = ordered.map((s) => {
      if (s.keyMode === 'entity') {
        // clave por ID del registro: estos campos no se rellenan desde este formulario
        return { questId: s.questId, name: name(s), priority: prio(s), text: t('rc.key.byEntity', { q: hasText(s.idAlias) ? q(s.idAlias) : t('rc.key.none') }),
          level: !hasText(s.idAlias) && s.enabled ? 'danger' : 'text' };
      }
      const qa = isId ? s.idAlias : s.originAlias;
      const v = isId ? null : hasText(s.originText) ? s.originText.trim() : null;
      const text = hasText(qa) && v ? t('rc.key.both', { q: q(qa), v }) : hasText(qa) ? t('rc.key.q', { q: q(qa) })
        : v ? t('rc.key.text', { v }) : t('rc.key.none');
      // el motor valida la clave de todas las fuentes activadas: si falta en una, no se ejecuta nada
      const missing = !hasText(qa) && !v;
      return { questId: s.questId, name: name(s), priority: prio(s), text, level: missing && s.enabled ? 'danger' : 'text' };
    });
    say('K', 'rc.key.match');
    if (ordered.some((s) => s.keyMode === 'entity')) say('K', 'rc.key.entityNote', null, 'info');
    say('K', 'rc.key.spelling', null, 'info');
    say('K', 'rc.key.error', null, 'info');
    return finish({
      status: keyRows.some((r) => r.level === 'danger') ? 'danger' : 'key', compact: noCompact,
      line: sentences[0].text, perForm: [], keyRows, example: null,
    });
  }

  // 2. Sin conexiones
  if (!chain.length) {
    say('A', 'rc.none');
    return finish({ status: 'none', compact: noCompact, line: t('rc.none'), perForm: [], keyRows: null, example: null });
  }

  // 3. Qué trae cada formulario (cadena efectiva: hasta el primer dato de la encuesta, que siempre viene)
  const sysPos = chain.findIndex(isSys);
  const hasSys = sysPos >= 0;
  const sys = hasSys ? chain[sysPos] : null;
  const eff = hasSys ? chain.slice(0, sysPos + 1) : chain;
  const rows = ordered.map((s) => {
    const set = s.enabled ? formAliases(s.questId) : null;
    const off = !s.enabled ? 'off' : !set ? 'missing' : null;
    const brings = off ? [] : eff.filter((a) => isSys(a) || set.has(a));
    return { s, set, brings, state: off || (brings.length ? 'feeds' : 'silent') };
  });
  const F = rows.filter((r) => r.state === 'feeds');
  const S = rows.filter((r) => r.state === 'silent');
  const n = F.length;
  const N = n + S.length;
  const dead = chain.filter((a) => !isSys(a) && ![...F, ...S].some((r) => r.set.has(a)));
  const ladder = [...new Set(F.map((r) => prio(r.s)))].sort((a, b) => a - b);
  const kind = eff.length > 1 ? 'aN' : isSys(eff[0]) ? 'sys' : 'a1';
  const q0 = q(eff[0]);
  const what = kind === 'a1' ? t('rc.what.a1', { q: q0 }) : t('rc.what.aN');
  const code = rows.find((r) => r.state === 'feeds' || r.state === 'silent')?.s.questId ?? 'SUR-0000000434';
  const sysKey = sys ? sys.slice(1) : '';

  // 4. A · de dónde
  if (N === 0) say('A', 'rc.noSources', null, 'warn');
  if (kind === 'aN') say('A', 'rc.src.aN', { chain: chainOf(eff) });
  if (N > 0) {
    const forms = F.map((r) => name(r.s));
    if (n === 0) say('A', 'rc.src.dead', { what2: kind === 'a1' ? what : t('rc.what2.aN') }, 'warn');
    else if (kind === 'sys') say('A', 'rc.src.sys', { sys: t('sys.' + sysKey, { code }) });
    else if (kind === 'a1') {
      if (n === N) say('A', N === 1 ? 'rc.src.a1.f1' : 'rc.src.a1.all', { q: q0, form: forms[0], n });
      else if (n === 1) say('A', 'rc.src.a1.one', { q: q0, N, form: forms[0] });
      else say('A', 'rc.src.a1.some', { q: q0, k: n, N, forms: listOf(forms) });
    } else {
      const partial = F.filter((r) => r.brings.length < eff.length);
      if (!partial.length && n === N && n >= 2) say('A', 'rc.src.aN.all', { n });
      for (const r of partial) say('A', 'rc.src.partial', { form: name(r.s), list: listOf(r.brings.map(q)) });
    }
  }
  if (S.length) {
    const forms = S.map((r) => name(r.s));
    const params = { form: forms[0], forms: listOf(forms), what };
    if (clear) say('A', S.length === 1 ? 'w.clearSilent1' : 'w.clearSilentN', params, 'danger');
    else if (n > 0) say('A', S.length === 1 ? 'rc.src.silent1' : 'rc.src.silentN', params);
  }
  if (n >= 1) for (const a of dead) say('A', 'w.dead', { q: q(a) }, 'warn');
  if (hasSys && sysPos < chain.length - 1) say('A', 'w.afterSys', { sysCap: t('sysCap.' + sysKey), rest: chainOf(chain.slice(sysPos + 1)) }, 'warn');

  // 5. C · conversión
  if (isText) say('C', 'rc.conv.text');
  else if (fam === 'unsupported') say('C', 'rc.conv.unsupported', { type }, 'danger');
  else {
    say('C', 'rc.conv.' + fam);
    if (eff.some((a) => !isSys(a))) say('C', 'rc.conv.multi', { type }, 'info');
    if (eff.length >= 2) say('C', 'rc.conv.firstOnly', null, 'info');
    say('C', 'rc.conv.error', null, 'info');
  }
  if ((sys === '@quest_id' || sys === '@response_id') && !isText) say('C', 'w.sysType', { sysCap: t('sysCap.' + sysKey), type }, 'danger');
  if (sys === '@response_date' && ['int', 'dec', 'bool', 'unsupported'].includes(fam)) say('C', 'w.sysTypeDate', { type }, 'danger');

  // 6. R · regla (apply: concat solo junta textos; en otro tipo escribe como last)
  const m = mode === 'concat' && !isText ? 'last' : mode;
  const many = n >= 2;
  if (m !== mode) say('R', 'w.concatNotText', { type }, 'warn');
  if (m === 'last') say('R', many ? 'rc.rule.last.fN' : 'rc.rule.last.f1');
  else if (m === 'first') {
    say('R', many ? 'rc.rule.first.fN' : 'rc.rule.first.f1');
    say('R', 'rc.rule.first.existing', null, 'info');
  } else if (m === 'priority') {
    if (n === 1) say('R', 'rc.rule.prio.one', { form: name(F[0].s) }, 'info');
    else if (many && ladder.length === 1) say('R', 'rc.rule.prio.flat', { n, p: ladder[0] }, 'warn');
    else if (many) {
      const steps = ladder.map((p) => {
        const at = F.filter((r) => prio(r.s) === p);
        return at.length === 1 ? t('rc.ladder.one', { p, form: name(at[0].s) }) : t('rc.ladder.many', { p, n: at.length });
      });
      say('R', 'rc.rule.prio.fN', { ladder: steps.join(' › ') });
      say('R', 'rc.rule.prio.noEvidence', null, 'info');
    }
  } else {
    say('R', many ? 'rc.rule.concat.fN' : 'rc.rule.concat.f1', { n });
    say('R', 'rc.rule.concat.existing', null, 'info');
  }

  // 7. M · sin valor (un dato de la encuesta siempre viene)
  if (hasSys) say('M', 'rc.miss.never', { sys: t('sysIn.' + sysKey) });
  else {
    say('M', `rc.miss.${clear ? 'clear' : 'keep'}.${kind}`, { q: q0 });
    if (clear && m === 'last') say('M', 'rc.miss.clear.last', null, 'info');
    else if (clear && m === 'first') say('M', 'w.firstClear', null, 'warn');
    else if (clear && m === 'priority' && N >= 2) say('M', 'w.prioClear', null, 'warn');
    else if (clear && m === 'concat') say('M', 'w.concatClear', null, 'warn');
  }

  // 8. X · extensión 1:1
  if (field.isExt) say('X', 'rc.ext', { table: field.table, root: field.rootName });

  // 9-10. Estado, compacto y resumen de una línea
  const status = STATUS[Math.max(0, ...sentences.map((s) => LEVELS[s.level]))];
  const cover = N >= 2 && n < N ? `${n}/${N}` : null;
  const ladderChip = ladder.slice(0, 3).map((p) => 'P' + p).join('›') + (ladder.length > 3 ? '›…' : '');
  const compact = {
    rule: mode === 'priority' && ladder.length >= 2 ? t('chip.priorityLadder', { ladder: ladderChip }) : t('chip.' + mode),
    clear: clear && !hasSys ? t('chip.clear') : null,
    cover,
    coverTitle: cover ? t('chip.coverTitle', { k: n, N }) : null,
  };
  const from = eff.length === 1 ? t('rc.from1', { q: q0 })
    : t(eff.length === 2 ? 'rc.from2' : 'rc.fromMore', { q: q0, q2: q(eff[1]), k: eff.length - 2 });
  const miss = t(hasSys ? 'rc.missNever' : clear ? 'rc.missClear' : 'rc.missKeep');
  const rule = t('merge.' + mode);
  const line = N >= 2
    ? t('rc.one', { rule, from, cover: t(n === N ? 'rc.coverAll' : 'rc.coverSome', { k: n, N }), miss })
    : t('rc.one.noCover', { rule, from, miss });

  // 11. Por formulario
  const perForm = rows.map((r) => ({
    questId: r.s.questId, name: name(r.s), priority: prio(r.s), state: r.state, uses: r.brings.map(q),
    onMissing: r.state === 'silent' ? (clear ? 'clearsAlways' : 'ignored') : hasSys ? 'never' : clear ? 'clear' : 'keep',
    leads: mode === 'priority' && ladder.length >= 2 && r.state === 'feeds' && prio(r.s) === ladder[0],
  }));

  // 12. Semilla del ejemplo: A = formulario de más prioridad que alimenta el campo, B = el de menos
  let example = null;
  if (n > 0) {
    const [vals, startValue] = SAMPLES[fam];
    const sysFirst = isSys(eff[0]);
    const A = F[0].s;
    const B = F[n - 1].s;
    const plan = n >= 2 ? [[B, 0], [A, 1], [B, 2], [A, null], [B, 3]] : [[A, 0], [A, 1], [A, 1], [A, null], [A, 2]];
    example = {
      family: fam, isText, sys, sysFirst, typeName: type, startValue,
      events: plan.map(([s, v], i) => ({ day: DAYS[i], dayLabel: dayLabel(DAYS[i]), questId: s.questId, prio: prio(s), raw: sysFirst || v == null ? null : vals[v] })),
    };
  }

  return finish({ status, compact, line, perForm, keyRows: null, example });
}
